/**
 * 시스템 설정 — 「서버 점검」 탭(EA-68)·켜기·끄기 창(EA-69).
 *
 * 요구: 사장님 2026-10-01 「점검 중 동작 -> 거래소 관리자에 서버 점검 버튼만들어서 서버 점검 상태를
 * 만들어서 테스트 하면되잖아」, 「맞지, 시작해」(거래 흐름 04 시험). 서버 몫은 거래소백엔드 `8d60143`
 * (거래소 01 6-34).
 *
 * 계약: XM-104(상태 — `settings.view`)·XM-105(켜기·끄기 — `settings.edit` + 최고관리자만). 단추는 XM-104 의
 * `canToggle` 이 true 일 때만 보인다 — 사장님 2026-10-02 「최고관리자(admin) 한 사람만 서버점검 누를 수 있다.」(그 전
 * 2026-10-01 질문 38 「총괄 관리자만 승인가능」 — 다른 관리자에게 '설정 바꾸기' 권한을 줘도 이 단추는 안 보임). 누가
 * 누를 수 있는지는 서버가 가린다(거래소백엔드 5758ccc — 최고관리자만). 그 밖의 관리자가 보내면 서버가 403
 * `forbidden`(`reasonCode` `super_admin_only`).
 *
 * 켜면 거래소 점검(USDE 멈춤·체인 장애와 같은 점검, 까닭 `SERVER_MAINTENANCE`)이 열린다 — 앱·간편결제·
 * 팝업은 503 `exchange_halted`, 지갑 쪽은 503 `unavailable`. 관리자 창구는 점검 중에도 답하므로 이 탭에서
 * 끌 수 있다. 이 단추는 서버 점검만 켜고 끈다 — USDE 멈춤·체인 장애 점검은 닫지 않는다(XM-105).
 *
 * ⚠ 보낼 값은 단추를 누른 때 본 상태의 반대다(꺼져 있으면 켜기). 그 사이 다른 관리자가 바꿨어도 같은
 * 값을 다시 보내는 것이라 서버는 아무것도 바꾸지 않는다(XM-105 멱등). 끝나면 XM-104 를 다시 읽는다.
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getMaintenance, setMaintenance } from '@/api/admin'
import { setFlash } from '@/auth/pendingWrite'
import { ApiError } from '@/api/errors'
import { Badge, Button, Labeled, Maybe, Notice, Row, Time } from '@/components/ui'
import { Confirm, submitWrite, useWrite } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { HALT_REASON_LABEL } from '@/api/types'

/** 탭 이름 — 사장님 말 「서버 점검」 그대로. */
export const MAINTENANCE = 'Server maintenance'

/** 서버 점검이 켜져 있는 동안 앱 쪽에 보이는 글(거래소백엔드 `ExchangeHalt.MAINTENANCE_MESSAGE`, 계약 XM-105). */
const APP_MESSAGE = 'The server is under maintenance. Please try again later.'

/** EA-68 — 서버 점검 탭(XM-104). */
export function MaintenanceTab() {
  const q = useQuery({ queryKey: ['maintenance'], queryFn: getMaintenance, staleTime: 0 })
  // 단추를 누른 때 고른 값 — true 면 켜기 창, false 면 끄기 창.
  const [target, setTarget] = useState<boolean | null>(null)
  const s = q.data

  return (
    <>
      <p className="mb-4 text-xs text-slate-500">
        Turning server maintenance on puts the exchange under maintenance. The admin screens still work during
        maintenance, so it can be turned off here. Turning it on and off is recorded in the audit log.
      </p>
      <QueryState isPending={q.isPending} error={q.error}>
        {s && (
          <>
            <dl className="mb-4 grid grid-cols-[8rem_1fr] gap-1.5 text-sm">
              <Row label="Server maintenance">
                {s.maintenance ? <Badge tone="stop">On</Badge> : <Badge>Off</Badge>}
              </Row>
              <Row label="Turned on at"><Time ms={s.since} /></Row>
              <Row label="Exchange">
                {s.halted ? <Badge tone="stop">Under maintenance</Badge> : <Badge tone="ok">Open</Badge>}
              </Row>
              {s.halted && (
                <>
                  <Row label="Reason"><Labeled labels={HALT_REASON_LABEL} code={s.reason} /></Row>
                  <Row label="Text shown in the apps"><Maybe value={s.message} /></Row>
                </>
              )}
              <Row label="Server time"><Time ms={s.serverTime} /></Row>
            </dl>
            {!s.maintenance && s.halted && (
              <div className="mb-4">
                <Notice tone="warn">
                  Server maintenance is off, but maintenance for another reason is open. This button does not close
                  that maintenance.
                </Notice>
              </div>
            )}
            {s.canToggle === true && (
              <Button onClick={() => setTarget(!s.maintenance)}>
                {s.maintenance ? 'Turn off server maintenance' : 'Turn on server maintenance'}
              </Button>
            )}
          </>
        )}
      </QueryState>
      {target !== null && <MaintenanceDialog on={target} onClose={() => setTarget(null)} />}
    </>
  )
}

/** EA-69 — 서버 점검 켜기·끄기 창(XM-105). 되돌릴 수 있는 일이라 「되돌릴 수 없습니다」를 붙이지 않는다. */
function MaintenanceDialog({ on, onClose }: { on: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const label = on ? 'Turn on server maintenance' : 'Turn off server maintenance'

  async function submit() {
    const send = async () => {
      await setMaintenance(on)
      setFlash({ tone: 'ok', text: on ? 'Server maintenance is on.' : 'Server maintenance is off.' })
      await qc.invalidateQueries({ queryKey: ['maintenance'] })
    }
    await submitWrite(w, label, send, onClose, {
      // 최고관리자가 아니라고 서버가 막으면(403 super_admin_only) 상태를 다시 읽는다 — canToggle 이 false 면
      // 단추가 사라진다(그 사이 서버 판정이 바뀐 경우 — 2026-10-02 처럼).
      onError: (err) => {
        if (err instanceof ApiError && err.reasonCode === 'super_admin_only') {
          void qc.invalidateQueries({ queryKey: ['maintenance'] })
        }
      },
    })
  }

  return (
    <Confirm open title={label} action={on ? 'Turn on' : 'Turn off'} danger irreversible={false}
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}>
      {on ? (
        <div className="flex flex-col gap-2">
          <p>
            The exchange goes under maintenance. The app, easy payment and the popup receive &quot;{APP_MESSAGE}&quot;,
            and wallet-side requests are blocked too. Cancelling waiting orders is blocked too.
          </p>
          <p>
            The clocks of the signing deadline, the payment window and the first collateral deadline stop, and chain
            transfers wait until maintenance ends.
          </p>
          <p>The admin screens still work during maintenance, so it can be turned off here.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p>
            Closes server maintenance. If there is no maintenance for another reason (USDE paused, chain outage), the
            exchange opens again and the clocks (signing deadline, payment window, first collateral deadline) move
            forward by the time they were stopped.
          </p>
          <p>This button does not close maintenance for USDE paused or a chain outage.</p>
        </div>
      )}
    </Confirm>
  )
}
